import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router'
import { AuthService } from './services/auth.service'
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions'
import { select, Store } from '@ngrx/store'
import {
  LocalStorage,
  selectAccountsLoaded,
  selectSomeOnce,
  Toaster,
  selectCurrentAccount,
  isSomething,
  User,
  Account,
  DataCheckerService,
  AccountGuard,
} from '@tokeet/tv3-platform'
import { filter, share, switchMap, switchMapTo, take, tap, delay } from 'rxjs/operators'
import * as fromRoot from '@tv3/store/state'
import { IntercomService } from '@tv3/services/intercom.service'
import { SkipMasterLayoutService } from '@tv3/services/utils/skip-master-layout.service'
import { WootricService } from '@tv3/services/wootric.service'
import { UserPreferenceService } from '@tv3/store/user-preferences/user-preferences.service'
import { VisitTV3 } from './store/user-preferences/user-preferences.actions'
import {
  selectUserPreferences,
  selectUserPreferencesLoaded,
} from '@tv3/store/user-preferences/user-preferences.selectors'
import * as Sentry from '@sentry/browser'
import { SwUpdate } from '@angular/service-worker'
import { combineLatest, interval, of } from 'rxjs'
import { RefreshInquiries } from '@tv3/store/inquiry/inquiry.actions'
import { selectActivePlansLoaded, selectActiveTokeetPlanView } from '@tv3/store/plan/plan.selectors'
import { LoadActivePlans } from '@tv3/store/plan/plan.actions'
import { AnnouncementService } from '@tv3/services/announcement.service'
import { MatSnackBar } from '@angular/material/snack-bar'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { GoogleTagManagerService } from './services/google-tag-manager.service'
import { RoutingService } from '@tv3/services/routing.service'
import { PhoneNumberRequestDialogService } from './shared/phone-number-request-dialog/phone-number-request-dialog.service'
import { PlanSubscriptionView } from './store/plan/plan.view'
import { SessionStorage } from './services/utils/storage.service'
import { ADMIN_MASQUERADE_FLAG_KEY } from './services/utils/storage-prefix'

import 'sortable-tablesort/sortable.min.js'
import { RentalGuard } from './guards/rental.guard'
import { UserPreferencesGuard } from './guards/user-preference.guard'
import { EntityTagsGuard } from '@tokeet-frontend/tags'

declare const window: any
export const CHUNK_ERROR_KEY = 'CHUNK_ERROR_KEY'

@Component({
  selector: 'app-root',
  template: `
    <ng-progress></ng-progress>
    <app-sidebar></app-sidebar>
    <app-navbar></app-navbar>
    <section #mainSection [ngClass]="appMainClass">
      <div class="app-banner-container mx-3 mb-3" style="margin-top:-15px;" [ngClass]="globalAlertClass">
        <app-acm-banner2 *ngIf="!skipMasterLayout.check()" class="shadow-sm d-block"></app-acm-banner2>
        <alert type="warning" class="d-block" *ngIf="!skipMasterLayout.check() && isReadonly">
          You don’t have access to change anything on this account
        </alert>
        <alert type="warning" class="d-block" *ngIf="!skipMasterLayout.check() && (delinquent$ | async)">
          <strong>Payment Failed:</strong> Your account is currently delinquent. Please update your card to ensure the
          account is running smoothly.
        </alert>
      </div>
      <router-outlet></router-outlet>
    </section>
    <ng-container *ngIf="authenticatedPageLoaded$ | async">
      <app-phone-widget *ngIf="isFirstPaid$ | async"></app-phone-widget>
      <app-help-widget></app-help-widget>
    </ng-container>
    <app-acm-supa-demo-starter></app-acm-supa-demo-starter>
  `,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  @ViewChild('mainSection', { static: true }) mainSection: ElementRef

  pageNavigationEnd$ = this.router.events.pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
  pageNavigationStart$ = this.router.events.pipe(filter((event: RouterEvent) => event instanceof NavigationStart))

  authenticatedPageLoading$ = this.pageNavigationStart$.pipe(
    filter(() => !this.skipMasterLayout.check()),
    filter(() => this.authService.isAuthenticated),
    share()
  )
  authenticatedPageLoaded$ = this.pageNavigationEnd$.pipe(
    filter(() => !this.skipMasterLayout.check()),
    filter(() => this.authService.isAuthenticated),
    share()
  )

  isFirstPaid$ = this.routingService.isFirstPaid()
  delinquent$ = this.routingService.isDelinquent()

  appMainClass = ''

  constructor(
    private el: ElementRef,
    private router: Router,
    private store: Store<fromRoot.State>,
    private ngxPermissionsService: NgxPermissionsService,
    private ngxRolesService: NgxRolesService,
    public skipMasterLayout: SkipMasterLayoutService,
    private authService: AuthService,
    private userPreferenceService: UserPreferenceService,
    private intercomService: IntercomService,
    private googleTagManagerService: GoogleTagManagerService,
    private swUpdate: SwUpdate,
    private snackbar: MatSnackBar,
    private announcementService: AnnouncementService,
    private storage: LocalStorage,
    private routingService: RoutingService,
    private toaster: Toaster,
    private wootricService: WootricService,
    private amplitudeService: AmplitudeService,
    private dataGuard: DataCheckerService,
    private sessionStorage: SessionStorage,
    private phoneNumberRequestDialog: PhoneNumberRequestDialogService
  ) {}

  get isMainPageScrollable() {
    const routes: [string, boolean][] = [
      ['inquiries/inbox', false],
      ['rates', true],
      ['calendar/multi', true],
    ]

    return !routes.find((route) => this.router.isActive(route[0], route[1]))
  }

  get globalAlertClass() {
    const routes: [string, boolean, string][] = [
      ['inquiries/inbox', false, 'inbox'],
      ['rates', true, 'rates-calendar'],
      ['calendar/planning', true, 'planning-calendar'],
      ['calendar/multi', true, 'multi-calendar'],
      ['channels', true, 'channels'],
    ]
    const r = routes.find((route) => this.router.isActive(route[0], route[1]))
    return r?.[2]
  }

  get isReadonly() {
    return this.authService.isReadOnly()
  }

  ngOnInit() {
    const oldHandler = this.router.errorHandler
    // Replace route error handler
    this.router.errorHandler = (err: any) => {
      // Check if there is an error loading the chunk
      if (err.stack && err.stack.indexOf('Error: Loading chunk') >= 0) {
        // Check if is the first time the error happened
        if (this.storage.get(CHUNK_ERROR_KEY) !== err.stack) {
          // Save the last error to avoid an infinite reload loop if the chunk really does not exists after reload
          this.storage.set(CHUNK_ERROR_KEY, err.stack)
          this.toaster.info('New version of the app detected, reloading...')
          setTimeout(() => {
            window.location.reload(true)
          }, 2500)
        } else {
          // The chunk really does not exist after reload
          console.error(`We cannot find the chunk...`)
        }
      }
      // Run original handler
      oldHandler(err)
    }

    this.swUpdate.available.subscribe(() => {
      const snack = this.snackbar.open('Update Available', 'Reload')
      snack.onAction().subscribe(() => {
        window.location.reload()
      })
    })

    if (this.authService.isAuthenticated) {
      this.intercomService.boot(this.authService.user)
      this.googleTagManagerService.update(this.authService.user)
      this.amplitudeService.setUser(this.authService.user)
      this.wootricService.run(this.authService.user)

      Sentry.configureScope((scope) => {
        const id = this.authService.user?.account?.toString() || ''
        scope.setUser({ id })
      })
    }

    this.authService.verify().subscribe()

    this.pageNavigationEnd$
      .pipe(
        tap(() => (this.mainSection.nativeElement.scrollTop = 0)),
        switchMap(() => this.routingService.isDelinquent())
      )
      .subscribe((isDelinquent) => {
        const classes = []
        if (!this.skipMasterLayout.check()) {
          classes.push('main-body')
        }

        if (!this.isMainPageScrollable) {
          classes.push('no-scroll')
        }
        let banners = 1 // acm banner
        classes.push('acm-banner')

        if (this.isReadonly) {
          classes.push('global-alert')
          banners++
        }
        if (isDelinquent) {
          classes.push('delinquent')
          banners++
        }
        classes.push(`banners-${banners}`)
        this.appMainClass = classes.join(' ')
        this.amplitudeService.logPageView()
      })

    this.authenticatedPageLoading$.pipe(take(1)).subscribe(() => {
      this.dataGuard.check([RentalGuard, AccountGuard, UserPreferencesGuard, EntityTagsGuard])
    })

    this.authenticatedPageLoaded$
      .pipe(
        switchMap(() =>
          this.store.pipe(
            select(selectUserPreferencesLoaded),
            filter((isLoaded) => isLoaded && !this.authService.hasRole('readOnly')),
            take(1)
          )
        ),
        switchMap(() => this.store.pipe(selectSomeOnce(selectUserPreferences)))
      )
      .subscribe((preferences) => {
        if (!preferences.tv3Login) {
          // this.videoDialogService.open()
          this.store.dispatch(VisitTV3())
        }
      })

    this.authenticatedPageLoaded$
      .pipe(delay(2000), switchMapTo(this.store.pipe(select(selectActivePlansLoaded))), take(1))
      .subscribe((isLoaded) => {
        if (!isLoaded) {
          this.store.dispatch(LoadActivePlans())
        }
      })

    this.authenticatedPageLoaded$
      .pipe(
        switchMapTo(this.store.pipe(select(selectAccountsLoaded))),
        filter((loaded) => !!loaded),
        take(1),
        switchMapTo(this.store.pipe(select(selectCurrentAccount)))
      )
      .subscribe((account) => {
        const user = this.authService.user
        const phone = isSomething(user.phone) ? user.phone : account.phone
        this.intercomService.update({ ...user, phone })
        this.googleTagManagerService.update({ ...user, phone } as User)
        this.amplitudeService.setUser({ ...user, phone } as User)
      })

    this.authenticatedPageLoaded$
      .pipe(
        take(1),
        switchMap(() => interval(90 * 1000))
      )
      .subscribe(() => {
        this.store.dispatch(RefreshInquiries())
      })

    combineLatest([
      this.store.pipe(select(selectCurrentAccount), filter(isSomething)),
      this.store.pipe(select(selectActiveTokeetPlanView), filter(isSomething)),
    ])
      .pipe(
        take(1),
        filter(([_, sub]: [Account, PlanSubscriptionView]) => sub.isPaid && sub.status === 'active'),
        switchMap(([account]) => {
          const user = this.authService.user
          const masquerade: boolean = this.sessionStorage.get(ADMIN_MASQUERADE_FLAG_KEY)
          if (!masquerade && this.authService.isAdmin() && (!account.phone || !user.phone)) {
            return this.phoneNumberRequestDialog.open().afterClosed()
          } else {
            return of(true)
          }
        })
      )
      .subscribe()
  }
}
